/* eslint-disable max-lines */
import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import isNumeric from 'validator/lib/isNumeric';
import isEmpty from 'validator/lib/isEmpty';
import moment from 'moment';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import HelpIcon from '@material-ui/icons/Help';
import FlagIcon from '@material-ui/icons/Flag';
import SendIcon from '@material-ui/icons/Send';
import Grid from '@material-ui/core/Grid';
import Feedback from '@material-ui/icons/Feedback';
import { Tooltip } from '@material-ui/core';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import classNames from 'classnames';

import { translate } from 'services/i18nService';
import AccessLevelService, { free, goldPromo } from 'services/AccessLevelService';
import LocalStorageService from 'services/LocalStorageService';
import ModalService from 'services/ModalService';
import NotificationService from 'services/NotificationService';
import SessionStorageService from 'services/SessionStorageService';
import TippingService from 'services/TippingService';
import ChatStore from 'stores/ChatStore';
import FirebaseStore from 'stores/FirebaseStore';
import GamesStore from 'stores/GamesStore';
import GroupShowStore from 'stores/GroupShowStore';
import { useStores } from 'stores/StoreContextProvider';
import ProfileStore from 'stores/ProfileStore';
import TippingStore from 'stores/TippingStore';
import UserStore from 'stores/UserStore';
import PerformerGoal from 'containers/PerformerGoal/PerformerGoal';
import Button from 'components/Button/Button';
import DesktopSecondTippingRow from 'components/DesktopSecondTippingRow/DesktopSecondTippingRow';
import DesktopPrivateShowMenu from 'components/DesktopPrivateShowMenu/DesktopPrivateShowMenu';
import APIStates from 'enum/APIStates';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import dates from 'enum/dates';
import { USER_MODALS_GROUP } from 'enum/helpers';
import { SECOND_AS_MILLISECONDS } from 'enum/timeFormat';
import allUserGroups from 'enum/userGroups';
import { BROADCAST_STATUS, NEW_SHOWS_STATE, VIDEO_AND_CHAT_CLASS } from 'enum/videoPlayer';
import { generateID, noOp } from 'utils/helpers';
import useInterval from 'hooks/useInterval';
import ThemeService from 'themes/ThemeService';
import { RegistrationModalCaller } from 'pages/RegistrationModal/utils';
import { showTippingAnimation } from 'themes/birthday/animations/tippingAnimation';

import { FIREBASE_AUTH_STATUS } from '../../firebase/firebaseStructure';
import tokenIcon from '../../../images/icontokens.png';

import ShowInteractionButtons from './ShowInteractionButtons';
import styles from './DesktopTipBar.css';


const IDS = {
  prefix: 'NeKiStrI',
  element: 'DesktopTipBar',
  buyTokens: 'buyTokens',
  tip: 'tip',
  customInputField: 'customInputField',
  sendCustomTip: 'sendCustomTip',
  helpBtn: 'helpBtn',
  reportBtn: 'reportBtn',
  feedbackBtn: 'feedbackBtn',
  tipMenuButton: 'tipMenuButton',
};

const makeId = generateID(IDS.prefix, IDS.element);
// eslint-disable-next-line no-magic-numbers
const tippingValues = [5, 10, 25, 50];

// eslint-disable-next-line complexity
function DesktopTipBar(props) {
  const [customTipValue, setCustomTipValue] = useState('');
  const [gameTimer, setGameTimer] = useState(null);
  const {
    user,
    showBuyTokensModal,
    roomName,
    handlePrivateShowAction,
    isGroupShowBarVisible,
    setShowRegistrationModal,
    isChatLocked,
    embedProfileV2,
  } = props;
  const { PaymentStore } = useStores();
  const { balance } = PaymentStore;
  const { setProfileChatTabValue } = ChatStore;
  const { isPerformerGoalVisible } = TippingStore;
  const {
    broadcastStatus,
    isPlayerBroadcastMode,
    privateShowEnded,
    profileFeedbackSent,
    spyShowEnded,
    realityShow,
  } = ProfileStore;
  const { roomDataReady, isPrivateRoom, authData, newShowsState, userKickedStatus, isBanned } = FirebaseStore;
  const { gameName, gamePaused, pauseTime, tipMenuActive } = GamesStore;
  const isSpyingShow = broadcastStatus === BROADCAST_STATUS.SPYING && !spyShowEnded;
  const transitionToPrivateShow =
    (broadcastStatus === BROADCAST_STATUS.ACCEPTED_INSIDE_PS || isSpyingShow) && !privateShowEnded;
  const guestPrivateRoom = authData.status === FIREBASE_AUTH_STATUS.privateRoom && !user.username;
  const { hasCF, hasUserModal, hasSubmittedModal } = UserStore;
  const videoAndChatWrap = document.querySelector(`.${VIDEO_AND_CHAT_CLASS}`);

  const shouldShowFeedbackModal =
    user.username &&
    AccessLevelService.isMin(free, props.user.accessLevel) &&
    !AccessLevelService.isMin(goldPromo, user.accessLevel) &&
    hasCF(allUserGroups.PROFILES_V2_FEEDBACK_OVERLAY) &&
    !profileFeedbackSent &&
    (
      !hasUserModal(USER_MODALS_GROUP.PROFILE_FEEDBACK) ||
      (
        hasUserModal(USER_MODALS_GROUP.PROFILE_FEEDBACK) &&
        !hasSubmittedModal(USER_MODALS_GROUP.PROFILE_FEEDBACK)
      )
    );

  const [isMenuOpen, setIsMenuOpen] = useState(null);
  const shouldShowTipMenu = tipMenuActive && hasCF(allUserGroups.TIP_MENU_2021);

  useInterval(() => {
    const timeLeft = moment(pauseTime).diff(moment());
    const formatted = moment(timeLeft).format(dates.format.mmss);
    if (timeLeft <= 0 || !gamePaused) {
      setGameTimer(null);
    } else {
      setGameTimer(formatted);
    }
  }, [gamePaused ? SECOND_AS_MILLISECONDS : null]);

  const renderBuyTokenText = useCallback(() => {
    if (embedProfileV2) {
      return (
        <span>
          <span className={styles.embedBalance}>{balance}</span>
          {translate('v2profile.buy.tokens')}
        </span>
      );
    }

    if (balance > 0) {
      return balance;
    }

    return translate('v2profile.buy');
  }, [balance, embedProfileV2]);

  const showInteractionElement = useMemo(() => {
    const isRealityShow = Boolean(realityShow);
    if (isRealityShow) {
      return null;
    }

    return (
      <ShowInteractionButtons
        broadcasterName={roomName}
        className={styles.goPrivate}
        handlePrivateShowAction={handlePrivateShowAction}
        setShowRegistrationModal={setShowRegistrationModal}
        user={user}
      />
    );
  }, [
    handlePrivateShowAction,
    roomName,
    setShowRegistrationModal,
    user,
    realityShow,
  ]);

  const showNotification = () => {
    if (
      [
        BROADCAST_STATUS.ACCEPTED_INSIDE_PS,
        BROADCAST_STATUS.SPYING,
        BROADCAST_STATUS.INSIDE_GS,
        BROADCAST_STATUS.INSIDE_PS,
      ].includes(broadcastStatus)
    ) {
      NotificationService.create({
        type: NotificationService.type.TOAST,
        text: translate('v2profile.tipping.tip.sent'),
        subtype: NotificationService.subtype.SUCCESS,
      });
    }
  };

  const getTipData = (amount) => {
    const { username, accessHash } = user;
    const { tippingSessionId, kiirooRoom } = FirebaseStore;
    const { anonymousTippingHash } = TippingStore;
    const { chatSettings: { stealthModeValue } } = ChatStore;

    return {
      kiirooRoom,
      modelId: roomName,
      accessHash,
      amount,
      sessionId: tippingSessionId || '',
      stealthMode:
        stealthModeValue && stealthModeValue.split('|')[0] === 'hidden_anonymous' ? anonymousTippingHash : '',
      type: 'SEND_TIP',
      viewer: username,
    };
  };

  const onShowBuyTokensModal = useCallback(() => {
    if (embedProfileV2 && !user.username) {
      setShowRegistrationModal(true);
    } else {
      showBuyTokensModal();
    }
  }, [embedProfileV2, showBuyTokensModal, setShowRegistrationModal, user.username]);

  const quickTipPerformer = (amount) => {
    const { sendTip, TIPPING_TYPES } = TippingService;

    if (PaymentStore.balance >= parseInt(amount, 10)) {
      sendTip(getTipData(amount), TIPPING_TYPES.QUICK_TIP).then(showNotification);
      showTippingAnimation(amount);
    } else {
      onShowBuyTokensModal();
    }
  };

  const handleCustomTipChange = (e) => {
    const { value } = e.target;
    if (parseInt(value, 10) === 0) {
      e.preventDefault();
    } else if ((isNumeric(value, { 'no_symbols': true }) || isEmpty(value)) && value.length <= 7) {
      setCustomTipValue(value);
      LocalStorageService.set('customTipValue', value);
    }
  };

  const customTipPerformer = () => {
    if (customTipValue) {
      const { sendTip, TIPPING_TYPES } = TippingService;

      if (customTipValue.length > 0 && PaymentStore.balance >= parseInt(customTipValue, 10)) {
        sendTip(getTipData(customTipValue), TIPPING_TYPES.CUSTOM_TIP).then(showNotification);
        showTippingAnimation(customTipValue);
      } else {
        onShowBuyTokensModal();
      }
    }
  };

  const determineGameTabIndex = () => {
    const showPublicChatCF = UserStore.hasCF(allUserGroups.PROFILES_V2_SPECIAL_SHOW_PUBLIC_CHAT);
    const showGSchat =
      showPublicChatCF &&
      GroupShowStore.userHaveTicket(user.username) &&
      broadcastStatus === BROADCAST_STATUS.INSIDE_GS &&
      newShowsState === NEW_SHOWS_STATE.INSIDE_GS;

    if (!embedProfileV2) {
      return showGSchat ? 5 : 4;
    }

    if (SessionStorageService.get('wasUserInPrivateShow')) {
      return showGSchat ? 3 : 2;
    }

    return showGSchat ? 2 : 1;
  };

  const handleGameButton = () => {
    const { user: { username } } = props;
    const gameTabIndex = determineGameTabIndex();
    if (username) {
      setProfileChatTabValue(gameTabIndex);
    } else {
      onShowBuyTokensModal();
    }
  };

  const handleCloseMenu = () => {
    document.body.style.overflow = null;
    setIsMenuOpen(null);
  };

  const handleUserReport = () => {
    handleCloseMenu();
    const isGuestBilling = user.accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;
    const performerName = ProfileStore.performerDetails.username;

    if (isGuestBilling) {
      ModalService.showModal(ModalService.MODAL_KEYS.GUEST_BILLING_MODAL, {
        close: ModalService.hideModal,
        show: true,
      });

      return;
    }

    if (user.username) {
      ModalService.showModal(ModalService.MODAL_KEYS.CHAT_CONTEXTUAL_MENU_REPORT, {
        show: true,
        originalUsername: performerName,
      });
    } else {
      setShowRegistrationModal?.(true, RegistrationModalCaller.DESKTOP_TIP_BAR);
    }
  };

  const openContactPage = () => {
    handleCloseMenu();
    window.open('/contact', '_blank');
  };

  const handleCloseFeedbackModal = (actionName) => {
    const { addNewUserModal, user: username } = UserStore;
    const { handleSendProfileFeedbackResponseStatus } = ProfileStore;
    const data = {
      username,
      modalType: USER_MODALS_GROUP.PROFILE_FEEDBACK,
      action: actionName,
    };
    addNewUserModal(data);
    handleSendProfileFeedbackResponseStatus(APIStates.INITIAL);
    ModalService.hideModal();
  };

  const showFeedbackModal = () => {
    handleCloseMenu();
    ModalService.showModal(
      ModalService.MODAL_KEYS.PROFILE_V2_FEEDBACK_MODAL,
      { close: handleCloseFeedbackModal },
    );
  };

  if (isPrivateRoom || guestPrivateRoom) {
    return (
      <div className={styles.showInteractionAndTippingWrap}>
        <div className={styles.privateBroadcastInteractions}>
          {translate('v2profile.room.interactions')}
        </div>
      </div>
    );
  }

  const gameLabel = `${translate('v2profile.game.button.play')} ${gameTimer ? gameTimer : ''}`;

  const handleClick = (event) => {
    setIsMenuOpen(event.currentTarget);
  };

  const openTipMenu = () => {
    ModalService.showModal(
      ModalService.MODAL_KEYS.TIP_MENU_MODAL,
      { roomName, embedProfileV2, setShowRegistrationModal }
    );
  };


  const gameButtonClasses = classNames({
    [styles.gameButton]: true,
    [styles.gameButtonDisabled]: gamePaused,
  });

  const buyTokensWrap = classNames({
    [styles.buyTokensWrap]: !embedProfileV2,
    [styles.embedBuyTokensWrap]: embedProfileV2,
  });

  const wrapperClasses = classNames({
    [styles.showInteractionAndTippingWrap]: true,
    [styles.disabledArea]: isPlayerBroadcastMode,
  });

  if (userKickedStatus || isBanned) {
    return (
      <div className={styles.kickedOrBannedBackground}>
        <Button
          className={styles.grayTextLink}
          color="transparent"
          disabled={isPlayerBroadcastMode}
          id={makeId(IDS.reportBtn)}
          label={translate('v2profile.chat.report')}
          onClick={handleUserReport}
        />
      </div>
    );
  }

  return (
    <div className={wrapperClasses}>
      <Grid container={true}>
        {isPerformerGoalVisible && !transitionToPrivateShow && !isGroupShowBarVisible && roomDataReady && (
          <Grid item={true} md={12} sm={12} xs={12}>
            <PerformerGoal />
          </Grid>
        )}
        {transitionToPrivateShow && (
          <Grid item={true} md={12}>
            <DesktopPrivateShowMenu
              balance={Number(balance)}
              broadcasterName={roomName}
              handlePrivateShowAction={handlePrivateShowAction}
              isSpyingShow={isSpyingShow}
              showBuyTokensModal={showBuyTokensModal}
              user={user}
            />
          </Grid>
        )}
        <Grid classes={{ root: styles.tippingToolbar }} item={true} lg={12} md={12} sm={12} xs={12}>
          <div className={styles.tippingWrapper}>
            {showInteractionElement}

            <div className={styles.tippingWrap}>
              <div className={styles.quickTipWrap}>
                {tippingValues.map((tipOption) => (
                  <Button
                    key={tipOption}
                    className={styles.tipButton}
                    disabled={isPlayerBroadcastMode}
                    id={makeId(`${IDS.tip}${tipOption}`)}
                    label={tipOption.toString()}
                    onClick={() => quickTipPerformer(tipOption)}
                  />
                ))}
                {shouldShowTipMenu && (
                  <div className={styles.tipMenuButtonWrapper}>
                    <Button
                      className={styles.tipMenuButton}
                      colour="orange"
                      disabled={isPlayerBroadcastMode}
                      id={makeId(IDS.tipMenuButton)}
                      label={translate('v2profile.tip.menu')}
                      onClick={openTipMenu}
                    />
                  </div>
                )}
              </div>
              <div className={styles.customTipWrapper}>
                <input
                  autoComplete="off"
                  className={styles.textfieldRoot}
                  disabled={isPlayerBroadcastMode}
                  id={makeId(IDS.customInputField)}
                  placeholder={translate('v2profile.send.custom.tip.label')}
                  value={customTipValue}
                  onChange={handleCustomTipChange}
                />
                <Button
                  className={styles.sendCustomTip}
                  disabled={!customTipValue || isPlayerBroadcastMode}
                  icon={<SendIcon />}
                  id={makeId(IDS.sendCustomTip)}
                  onClick={customTipPerformer}
                />
              </div>
              {gameName && !isChatLocked && (
                <Tooltip arrow={true} title={gameLabel}>
                  <span className={styles.gameButtonWrapper}>
                    <Button
                      className={gameButtonClasses}
                      disabled={gamePaused || isPlayerBroadcastMode}
                      icon={<img alt="game icon" src={ThemeService.currentGameTheme.icon} />}
                      id={`${gameName}_openTab`}
                      onClick={gamePaused ? noOp : handleGameButton}
                    />
                  </span>
                </Tooltip>
              )}
            </div>
          </div>

          <div className={styles.secondPartOfToolbar}>
            <DesktopSecondTippingRow
              embedProfileV2={embedProfileV2}
              roomName={roomName}
              setShowRegistrationModal={setShowRegistrationModal}
              showBuyTokensModal={showBuyTokensModal}
              user={user}
            />
            <Tooltip
              classes={{ popper: balance > 0 ? styles.hide : null }}
              placement="top"
              title={translate('v2profile.buy.tokens.tooltip')}
            >
              <div className={buyTokensWrap} onClick={onShowBuyTokensModal}>
                <div className={styles.tippedAmount} id={makeId(IDS.buyTokens)}>
                  <span>
                    <img alt="tokens" src={tokenIcon} />
                    {renderBuyTokenText()}
                  </span>
                  <AddCircleIcon className={styles.addTokens} />
                </div>
              </div>
            </Tooltip>
            {!embedProfileV2 && (
              <div className={styles.horizMenu}>
                <Button
                  aria-controls="boostAndCompleteGoalMenu"
                  aria-haspopup="true"
                  className={styles.menuBtn}
                  disabled={isPlayerBroadcastMode}
                  onClick={handleClick}
                >
                  <MoreHorizIcon className={styles.menuIcon} />
                </Button>
                <Menu
                  anchorEl={isMenuOpen}
                  classes={{ paper: styles.boostCompleteMenuWrapper }}
                  container={videoAndChatWrap}
                  id="boostAndCompleteGoalMenu"
                  open={Boolean(isMenuOpen)}
                  onClose={handleCloseMenu}
                >
                  <MenuItem id={makeId(IDS.helpBtn)} onClick={openContactPage}>
                    <HelpIcon />
                    {translate('v2profile.chat.help')}
                  </MenuItem>

                  <MenuItem id={makeId(IDS.reportBtn)} onClick={handleUserReport}>
                    <FlagIcon />
                    {translate('v2profile.chat.report')}
                  </MenuItem>

                  {shouldShowFeedbackModal && (
                    <MenuItem id={makeId(IDS.feedbackBtn)} onClick={showFeedbackModal}>
                      <Feedback />
                      {translate('v2profile.feedback.button')}
                    </MenuItem>
                  )}
                </Menu>
              </div>
            )}
          </div>
        </Grid>
      </Grid>
    </div>
  );
}

DesktopTipBar.propTypes = {
  handlePrivateShowAction: PropTypes.func.isRequired,
  roomName: PropTypes.string.isRequired,
  showBuyTokensModal: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  embedProfileV2: PropTypes.bool,
  isChatLocked: PropTypes.bool,
  isGroupShowBarVisible: PropTypes.bool,
  setShowRegistrationModal: PropTypes.func,
};

DesktopTipBar.defaultProps = {
  embedProfileV2: false,
  isChatLocked: false,
  isGroupShowBarVisible: false,
  setShowRegistrationModal: undefined,
};

export default observer(DesktopTipBar);
